import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Divider,
  List,
  Progress,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useSignedUpload } from '../../hooks/useSignedUpload';
import { notifications } from '@mantine/notifications';
import { PantryVideoRecorder } from './PantryVideoRecorder';
import { importPantryFromUploadedVideo } from '../../services/pantryService';

interface ImportPantryVideoModalProps {
  opened: boolean;
  onClose: () => void;
  onImportStarted: () => void;
}

interface ImportState {
  video: Blob | null;
  error: string | null;
  importing: boolean;
}

export const ImportPantryVideoModal: React.FC<ImportPantryVideoModalProps> = ({
  opened,
  onClose,
  onImportStarted,
}) => {
  const [state, setState] = useState<ImportState>({
    video: null,
    error: null,
    importing: false,
  });
  const { user } = useAuth();
  const { upload, progress, uploading, reset: resetUpload } = useSignedUpload();

  const handleVideoReady = useCallback((video: Blob) => {
    setState(prev => ({
      ...prev,
      video,
      error: null,
    }));
  }, []);

  const handleVideoCleared = useCallback(() => {
    setState(prev => ({
      ...prev,
      video: null,
      error: null,
    }));
    resetUpload();
  }, [resetUpload]);

  const handleReset = useCallback(() => {
    setState({
      video: null,
      error: null,
      importing: false,
    });
    resetUpload();
  }, [resetUpload]);

  const handleStartImport = useCallback(async () => {
    if (!user || !state.video) return;

    setState(prev => ({ ...prev, importing: true, error: null }));

    try {
      const contentType = state.video.type || 'video/webm';
      const fileId = await upload(state.video, 'recording.webm', contentType);

      const token = await user.getIdToken();
      await importPantryFromUploadedVideo(token, fileId);

      notifications.show({
        title: 'Video import started',
        message: 'Your video is being processed. Items will appear at the top when ready for review.',
        color: 'blue',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      // Close modal and trigger refresh
      onImportStarted();
      onClose();
      handleReset();

    } catch (error: any) {
      console.error('Pantry video import error:', error);

      setState(prev => ({
        ...prev,
        importing: false,
        error: error.message || 'Failed to start video import. Please try again.',
      }));

      notifications.show({
        title: 'Import failed',
        message: error.message || 'Failed to start video import. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [user, state.video, upload, onImportStarted, onClose, handleReset]);

  const isProcessing = state.importing || uploading;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Import from video</Text>}
      size="lg"
      centered
      radius="lg"
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}
    >
      <Stack gap="md">
        {/* Error Display */}
        {state.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Import error"
            color="red"
            variant="light"
            radius="lg"
          >
            {state.error}
          </Alert>
        )}

        {/* Video Recorder / Upload */}
        <PantryVideoRecorder
          onVideoReady={handleVideoReady}
          onVideoCleared={handleVideoCleared}
          disabled={isProcessing}
        />

        {/* Upload Progress */}
        {uploading && (
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              Uploading video... {progress}%
            </Text>
            <Progress value={progress} size="sm" animated />
          </Stack>
        )}

        {/* Instructions */}
        {!state.video && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="How it works"
            color="blue"
            variant="light"
            radius="lg"
          >
            <List spacing="xs" size="sm">
              <List.Item>Record or upload a video of your pantry, fridge, or kitchen</List.Item>
              <List.Item>Slowly pan across shelves so items are clearly visible</List.Item>
              <List.Item>Our AI will identify food items from the video frames</List.Item>
              <List.Item>Imported items will be flagged for your review</List.Item>
            </List>
          </Alert>
        )}

        {/* Import Button */}
        {state.video && (
          <>
            <Divider />
            <Group justify="flex-end" mt="md">
              <Button
                onClick={handleStartImport}
                loading={isProcessing}
                size="md"
                disabled={isProcessing || !state.video}
              >
                {uploading
                  ? 'Uploading Video...'
                  : state.importing
                    ? 'Processing Video...'
                    : 'Import Items'}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
